import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { StatusRenderer, Task, TaskStatus } from '@lit/task';
import { DeviceService } from '@treasury/domain/services/device/device.service';
import { FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { exists } from '@treasury/utils';
import { type TemplateResult, html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import './components/tm-blocking-loader';
import { NotificationService } from './services/notification';
import { TmBaseComponent } from './tm-base.component';

/**
 * Represents a component which has asynchronous work associated with its rendering lifecycle.
 * This class handles the boilerplate of displaying loading, error, and content states in a uniform way.
 *
 * Anything extending this component should call `tryFetch()` at least once.
 */
export abstract class TmContainer extends TmBaseComponent {
    @InjectProperty()
    declare protected readonly notificationService: NotificationService;

    @InjectProperty()
    declare protected readonly deviceService: DeviceService;

    @InjectProperty({
        async: true,
        token: FeatureFlagService,
    })
    declare protected featureFlagService: Promise<FeatureFlagService>;

    /**
     * Defaults to `true` to prevent bad states until `tryFetch()` is called.
     * It isn't reflected in `outstandingRequestCount` because nothing is in flight yet.
     * This is simpler than implementing a separate "initialized" state.
     */
    @state()
    private isLoading = true;

    /**
     * Tracks the number of active requests so that the container only finishes loading
     * after the last response is received, instead of the first response
     */
    private remainingRequestCount = 0;

    private tasks: Task[] = [];

    private readonly renderers = new WeakMap<Task, StatusRenderer<unknown>>();

    public get isDesktop() {
        return this.deviceService.isDesktop;
    }

    private get hasOutstandingTasks() {
        return this.tasks.some(t => t.status === TaskStatus.PENDING);
    }

    protected get loading() {
        return this.isLoading || this.hasOutstandingTasks;
    }

    protected set loading(val) {
        this.isLoading = val;
    }

    protected async tryFetch<RetVal = void>(
        /* Because of how this method is used by certain containers, we need an escape hatch to
        prevent calling the callback in case we don't have all the prerequisite data to make the
        fetch call yet. I chose `undefined` because I figured that would be less likely to be a
        valid response from the fetch call than `null`, and less cumbersome than using something
        like a symbol.
        
        If we end up in a situation where we need to use `undefined` as a value and still call the
        callback, we can revisit how to implement the escape hatch by using a symbol or a bool
        param to tryFetch that changes the behavior */
        fetcher: () => Promise<RetVal | undefined>,
        callback: (data: RetVal) => void,
        errorHandler?: (e: unknown) => void
    ) {
        if (this.remainingRequestCount === 0) {
            this.isLoading = true;
        }
        this.remainingRequestCount++;

        let result: RetVal | undefined;
        try {
            result = await fetcher();
            if (result !== undefined) {
                callback(result);
            }
        } catch (e) {
            const handler = errorHandler || ((e: unknown) => this.defaultErrorHandler(e));
            handler(e);
        } finally {
            this.remainingRequestCount--;
            if (this.remainingRequestCount === 0 && result !== undefined) {
                this.isLoading = false;
            }
        }
    }

    /**
     * Queue up asynchronous work for which to show appropriate, status-based UI state.
     *
     */
    protected enqueueWork<T, Deps extends ReadonlyArray<unknown> = ReadonlyArray<unknown>>(
        task: Task<Deps, T>,
        renderers: StatusRenderer<T> = {}
    ) {
        // we're using tasks, so allow their state to control the loading UI
        this.isLoading = false;

        if (!renderers.error) {
            renderers.error = e => this.defaultErrorHandler(e);
        }

        if (!renderers.pending) {
            renderers.pending = () => html`<tm-blocking-loader></tm-blocking-loader>`;
        }

        this.tasks.push(task);
        this.renderers.set(task, renderers as StatusRenderer<unknown>);
    }

    protected renderLoader() {
        if (this.isLoading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }

        if (this.hasOutstandingTasks) {
            const pendingTasks = this.tasks.filter(t => t.status === TaskStatus.PENDING);
            const renderers = pendingTasks.map(t => this.renderers.get(t)).filter(exists);
            const pendingRenderers = renderers.map(renderers => renderers.pending).filter(exists);

            return pendingRenderers.map(renderPending => renderPending()) as TemplateResult[];
        }

        return nothing;
    }

    private defaultErrorHandler(e: unknown) {
        this.notificationService.renderError(
            e instanceof Error ? e : 'An error occurred. Please try again.'
        );
    }
}
